import React, { useState, useContext, useEffect, useRef } from 'react';
import { Table } from "reactstrap";
import { AppContext } from '../application/provider';
import GestionExternoSistemasService from "../axios_services/gestionExternoSistemas.service";
import Loader from "../components/Loader/Loader";
import { ButtonSearch, ButtonInsert, ButtonUpdate, ButtonDelete } from '../components/Buttons/Buttons';
import ModalGestionExternoSistemas from '../components/Modal/ModalGestionExternoSistemas';
import './GestionServicioSistemas.css';

function GestionExternoSistemas() {
    const [state] = useContext(AppContext);
    const [registros, setRegistros] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRegistro, setSelectedRegistro] = useState(null);
    const initialFetchDone = useRef(false);

    const [estadosGestion] = useState(state.catalogos.tipos.filter(t => t.categoria_id == 41));
    const [validacionCatalog] = useState(state.catalogos.tipos.filter(t => t.categoria_id == 43));
    const [usuariosCatalog] = useState(state.user?.personal || []);

    const userProfile = state.catalogos?.perfiles?.find(p => String(p.perfil_id) === String(state.user?.perfil_id));
    const areaId = userProfile?.area_id || state.user?.area_id;

    const [periodo, setPeriodo] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [estadoFiltro, setEstadoFiltro] = useState('');

    useEffect(() => {
        if (!initialFetchDone.current) {
            loadInitialPageData();
            initialFetchDone.current = true;
        }
    }, []);

    const loadInitialPageData = async () => {
        setIsLoading(true);
        try {
            const response = await GestionExternoSistemasService.getGestionExterno(periodo, estadoFiltro);
            setRegistros(response.data || []);
        } catch (error) {
            console.error(error);
            setRegistros([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = async () => {
        await loadInitialPageData();
    };

    const handleNuevoRegistro = () => {
        setSelectedRegistro(null);
        setIsModalOpen(true);
    };

    const handleEditarRegistro = (registro) => {
        setSelectedRegistro(registro);
        setIsModalOpen(true);
    };

    const handleCloseModal = (shouldReload = false) => {
        setIsModalOpen(false);
        setSelectedRegistro(null);
        if (shouldReload === true) {
            loadInitialPageData();
        }
    };

    const handleSaveRegistro = async (registroData) => {
        setIsLoading(true);
        setIsModalOpen(false);
        try {
            let response;
            if (registroData.gestion_tramite_id) {
                response = await GestionExternoSistemasService.updateGestionExterno(registroData);
            } else {
                response = await GestionExternoSistemasService.insertGestionExterno(registroData);
            }
            await loadInitialPageData();
            return response.data || response;
        } catch (error) {
            setIsLoading(false);
            alert(error.response?.data?.message || 'Ocurrió un error al guardar.');
            throw error;
        }
    };

    const handleEliminarRegistro = async (registro) => {
        if (registro.estado == 2) {
            alert('No se puede eliminar un registro en estado "Atendido".');
            return;
        }

        if (!window.confirm('¿Está seguro de eliminar este requerimiento?')) return;
        setIsLoading(true);
        try {
            await GestionExternoSistemasService.deleteGestionExterno({ gestion_tramite_id: registro.gestion_tramite_id });
            await loadInitialPageData();
        } catch (error) {
            alert(error.response?.data?.message || 'Ocurrió un error al eliminar.');
        } finally {
            setIsLoading(false);
        }
    };

    const formatFecha = (fecha) => {
        if (!fecha) return '-';
        return fecha.split('T')[0];
    };

    if (isLoading) {
        return <Loader />;
    }

    const canManageOrRegister = Number(areaId) === 8 || [8].includes(Number(state.user?.perfil_id));

    const getRowStyle = (estado) => {
        if (estado == 2) return { backgroundColor: '#fef3c7' };
        if (estado == 3) return { backgroundColor: '#dcfce7' };
        return {};
    };

    return (
        <div className="caja-flujo-container">
            <h1>GESTIÓN DE TRÁMITE EXTERNO</h1>

            <div className="filtros">
                <div className="campo-fecha">
                    <label>Periodo:</label>
                    <input
                        type="month"
                        value={periodo}
                        onChange={(e) => setPeriodo(e.target.value)}
                    />
                </div>

                <div className="campo-fecha">
                    <label>Estado:</label>
                    <select
                        value={estadoFiltro}
                        onChange={(e) => setEstadoFiltro(e.target.value)}
                        className="form-control"
                    >
                        <option value="">Todos los estados</option>
                        {estadosGestion?.map((estado) => (
                            <option key={estado.tipo_id} value={estado.tipo_id}>
                                {estado.descripcion}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <ButtonSearch
                onClick={handleSearch}
                isLoading={isLoading}
            />

            <div className="nuevo-movimiento-section">
                <ButtonInsert onClick={handleNuevoRegistro} />
            </div>

            <div className="table-container">
                <Table>
                    <thead>
                        <tr>
                            <th style={{ width: '200px', minWidth: '200px' }}>Solicitante</th>
                            <th style={{ width: '200px', minWidth: '200px' }}>Cliente</th>
                            <th style={{ width: '120px', minWidth: '120px' }}>Fecha Registro</th>
                            <th style={{ width: '350px', minWidth: '350px' }}>Requerimiento</th>
                            <th>Validación</th>
                            <th>Comentario</th>
                            <th style={{ width: '120px', minWidth: '120px' }}>Fecha Inicio</th>
                            <th>Comentario Cliente</th>
                            <th style={{ width: '120px', minWidth: '120px' }}>Fecha Entrega</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {registros.length > 0 ? (
                            registros.map((registro) => {
                                const usuarioSolicitante = usuariosCatalog.find(u => u.usuario_id == registro.usuario_id_solicitante);
                                const validacionLabel = validacionCatalog.find(v => v.tipo_id == registro.validacion_id)?.descripcion;
                                const estadoLabel = estadosGestion.find(e => e.tipo_id == registro.estado)?.descripcion;
                                const rowStyle = getRowStyle(registro.estado);

                                return (
                                    <tr key={registro.gestion_tramite_id} style={rowStyle}>
                                        <td>{usuarioSolicitante ? usuarioSolicitante.nombre_completo_usuario.toUpperCase() : 'SISTEMA'}</td>
                                        <td>{registro.cliente || '-'}</td>
                                        <td>{formatFecha(registro.fecha_registro)}</td>
                                        <td style={{ whiteSpace: 'pre-wrap' }}>{registro.requerimiento}</td>
                                        <td>{validacionLabel || '-'}</td>
                                        <td style={{ whiteSpace: 'pre-wrap', minWidth: '200px' }}>{registro.comentario || '-'}</td>
                                        <td>{formatFecha(registro.fecha_inicio)}</td>
                                        <td style={{ whiteSpace: 'pre-wrap', minWidth: '200px' }}>{registro.comentario_cliente || '-'}</td>
                                        <td>{formatFecha(registro.fecha_entrega)}</td>
                                        <td>{estadoLabel || registro.estado}</td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                                <ButtonUpdate onClick={() => handleEditarRegistro(registro)} />
                                                {Number(state.user?.perfil_id) === 8 && (
                                                    <ButtonDelete onClick={() => handleEliminarRegistro(registro)} />
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={11} className="text-center text-gray-500 py-4">
                                    No hay requerimientos registrados.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <ModalGestionExternoSistemas
                isOpen={isModalOpen}
                registro={selectedRegistro}
                onSave={handleSaveRegistro}
                onClose={handleCloseModal}
                usuarioId={state.user?.usuario_id}
                perfilId={state.user?.perfil_id}
                estadosGestion={estadosGestion}
                validacionCatalog={validacionCatalog}
                usuariosCatalog={usuariosCatalog}
            />
        </div>
    );
}

export default GestionExternoSistemas;
